/**
 * Best Practice API Client
 * Uses in-memory token store only
 * Automatic token refresh via httpOnly cookies
 */

import axios from 'axios';
import { getAccessToken, setAccessToken } from './tokenManager';
import { refreshAccessToken } from './authService';

declare module 'axios' {
  export interface AxiosRequestConfig {
    _retry?: boolean;
  }
}

// Create axios instance with base URL
// Priority:
// 1. REACT_APP_API_URL environment variable (if set)
// 2. Relative path '/api' (for production behind Nginx)
// 3. Fallback to localhost:8080 (for local development without proxy)
const apiBase = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace(/\/$/, '')
  : (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8080');
const api = axios.create({
  baseURL: `${apiBase}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Required for httpOnly cookies
});

// Request interceptor - attach access token from memory
api.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  async (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle 401 with automatic refresh
let refreshPromise: Promise<string | null> | null = null;

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config as import('axios').AxiosRequestConfig | undefined;
    const status = error.response?.status;
    const isAuthPath = config?.url?.includes('/auth/');

    // Handle 401 Unauthorized - attempt token refresh
    if (status === 401 && config && !config._retry && !isAuthPath) {
      config._retry = true;

      // Single-flight refresh request
      if (!refreshPromise) {
        refreshPromise = refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
      }

      const newToken = await refreshPromise;
      if (newToken) {
        // Retry original request with new token
        config.headers = {
          ...(config.headers || {}),
          Authorization: `Bearer ${newToken}`,
        } as any;
        return api.request(config);
      }

      // Refresh failed - clear auth state
      setAccessToken(null);
    }

    // Extract error message
    const headerMessage =
      error.response?.headers?.['x-error-message'] ||
      error.response?.headers?.['X-Error-Message'];
    const bodyMessage = error.response?.data?.message;
    const errorMessage = headerMessage || bodyMessage || error.message || 'An error occurred';

    const err = new Error(errorMessage) as any;
    err.status = status;
    return Promise.reject(err);
  }
);

export default api; 